import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Container,
  useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { authService } from '../services/auth';
import { socketService } from '../services/socket';
import { loginStart, loginSuccess, loginRequiresTFA, loginFailure } from '../store/slices/authSlice';
import { useLayout } from '../contexts/LayoutContext';

interface LoginFormData {
  username: string;
  password: string;
}

interface TFAFormData {
  tfaCode: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { preferences } = useLayout();
  const isClassicTheme = preferences.themePreset === 'classic';
  const [showPassword, setShowPassword] = useState(false);
  const [showTFA, setShowTFA] = useState(false);
  const [tfaSessionId, setTfaSessionId] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{ username: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { control: loginControl, handleSubmit: handleLoginSubmit } = useForm<LoginFormData>({
    defaultValues: {
      username: process.env.NODE_ENV === 'development' ? 'daleitcp@mach0.net' : '',
      password: process.env.NODE_ENV === 'development' ? 'ddrgh' : ''
    }
  });

  const { control: tfaControl, handleSubmit: handleTFASubmit } = useForm<TFAFormData>({
    defaultValues: {
      tfaCode: ''
    }
  });

  const handleLogin = async (data: LoginFormData) => {
    setError(null);
    setLoading(true);
    dispatch(loginStart());

    try {
      const response = await authService.login(data.username, data.password);
      
      if (response.requiresTFA && response.sessionId) {
        dispatch(loginRequiresTFA({ sessionId: response.sessionId }));
        setTfaSessionId(response.sessionId);
        setCredentials({ username: data.username, password: data.password });
        setShowTFA(true);
      } else if (response.success && response.accessToken) {
        // Store auth info in Redux
        dispatch(loginSuccess({
          user: {
            id: response.loginId || '',
            partnerId: response.partnerId || '',
            loginId: response.loginId || '',
            email: data.username,
            name: response.userName || '',
            role: 'engineer'
          },
          accessToken: response.accessToken,
          refreshToken: response.refreshToken || '',
          sessionId: response.sessionId || ''
        }));
        
        // Connect to WebSocket with auth token
        socketService.connect({ token: response.accessToken, authType: 'legacy' });
        
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
      dispatch(loginFailure(err.message || 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTFA = async (data: TFAFormData) => {
    if (!tfaSessionId || !credentials) return;
    
    setError(null);
    setLoading(true);

    try {
      const response = await authService.verifyTFA(credentials.username, credentials.password, data.tfaCode, tfaSessionId);
      
      if (response.success && response.accessToken) {
        // Store auth info in Redux
        dispatch(loginSuccess({
          user: {
            id: response.loginId || '',
            partnerId: response.partnerId || '',
            loginId: response.loginId || '',
            email: credentials.username,
            name: response.userName || '',
            role: 'engineer'
          },
          accessToken: response.accessToken,
          refreshToken: response.refreshToken || '',
          sessionId: response.sessionId || ''
        }));
        
        // Connect to WebSocket with auth token
        socketService.connect({ token: response.accessToken, authType: 'legacy' });
        
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'TFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            IT Control Panel
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!showTFA ? (
            <Box component="form" onSubmit={handleLoginSubmit(handleLogin)} sx={{ mt: 1 }}>
              <Controller
                name="username"
                control={loginControl}
                rules={{ required: 'Username is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Username"
                    autoComplete="username"
                    autoFocus
                    error={!!error}
                    helperText={error?.message}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#3099c5 !important',
                        },
                        '&:hover fieldset': {
                          borderColor: '#3099c5 !important',
                        },
                      },
                      '& label.Mui-focused': {
                        color: '#3099c5 !important',
                      },
                    }}
                  />
                )}
              />
              
              <Controller
                name="password"
                control={loginControl}
                rules={{ required: 'Password is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    error={!!error}
                    helperText={error?.message}
                    disabled={loading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#3099c5 !important',
                        },
                        '&:hover fieldset': {
                          borderColor: '#3099c5 !important',
                        },
                      },
                      '& label.Mui-focused': {
                        color: '#3099c5 !important',
                      },
                    }}
                  />
                )}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  // Use classic blue colors if in classic theme
                  ...(isClassicTheme && {
                    backgroundColor: '#33344a',
                    '&:hover': {
                      backgroundColor: '#2b3e7d',
                    }
                  })
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleTFASubmit(handleTFA)} sx={{ mt: 1 }}>
              <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                Two-Factor Authentication Required
              </Typography>
              
              <Controller
                name="tfaCode"
                control={tfaControl}
                rules={{ 
                  required: 'TFA code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'TFA code must be 6 digits'
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="TFA Code"
                    placeholder="Enter 6-digit code"
                    autoComplete="one-time-code"
                    autoFocus
                    error={!!error}
                    helperText={error?.message}
                    disabled={loading}
                    inputProps={{ maxLength: 6 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: '#3099c5 !important',
                        },
                        '&:hover fieldset': {
                          borderColor: '#3099c5 !important',
                        },
                      },
                      '& label.Mui-focused': {
                        color: '#3099c5 !important',
                      },
                    }}
                  />
                )}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  // Use classic blue colors if in classic theme
                  ...(isClassicTheme && {
                    backgroundColor: '#33344a',
                    '&:hover': {
                      backgroundColor: '#2b3e7d',
                    }
                  })
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify'}
              </Button>
              
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setShowTFA(false);
                  setTfaSessionId(null);
                  setCredentials(null);
                  setError(null);
                }}
                disabled={loading}
              >
                Back to Login
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;